import { readonly, ref } from 'vue';
import { controlDeferred, type IControl } from '@eureka/utils/control';

const ENTRY_CACHE_MS = 60_000;
const ENTRY_RESOLUTION_TIMEOUT_MS = 10_000;
const _DEFAULT_CRONJOB_BOUNDARY = '2026-09-18';
const CRONJOB_FIRST_LOGIN_BOUNDARY =
    localStorage.getItem('CRONJOB_FIRST_LOGIN_BOUNDARY') || _DEFAULT_CRONJOB_BOUNDARY;

type MonitorEntryTarget = 'cron' | 'monitor';
type CronFrontendControl = Pick<IControl, 'cron_job_permission' | 'first_login_time'>;

let entryTargetRequest: Promise<MonitorEntryTarget> | undefined;
const entryTarget = ref<MonitorEntryTarget>();
// undefined 表示首次判断尚未完成，避免提前展示错误的入口。
export const monitorEntryTarget = readonly(entryTarget);
let entryTargetCheckedAt = 0;

/**
 * 定时任务前端灰度开关。
 *
 * 该字段仅控制前端功能曝光，真正权限仍由 Cron API 后端校验。
 */
export function hasCronJobFrontendPermission(control: CronFrontendControl): boolean {
    return Boolean(control.cron_job_permission);
}

function resolveMonitorEntryTarget(control: CronFrontendControl): MonitorEntryTarget {
    if (!hasCronJobFrontendPermission(control)) return 'monitor';
    const raw = control.first_login_time;
    if (!raw || typeof raw !== 'string') return 'monitor';
    const dateStr = raw.slice(0, 10);
    if (dateStr < CRONJOB_FIRST_LOGIN_BOUNDARY) return 'monitor';
    return 'cron';
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('monitor entry resolution timed out')), timeoutMs);
        promise.then(
            value => {
                clearTimeout(timer);
                resolve(value);
            },
            error => {
                clearTimeout(timer);
                reject(error);
            }
        );
    });
}

export async function resolveMonitorEntryTargetFromSources(
    controlPromise: Promise<CronFrontendControl>,
    timeoutMs = ENTRY_RESOLUTION_TIMEOUT_MS
): Promise<MonitorEntryTarget> {
    try {
        const control = await withTimeout(controlPromise, timeoutMs);
        return resolveMonitorEntryTarget(control);
    } catch (error) {
        console.error('[Cron] Failed to resolve the Monitor navigation entry:', error);
        return 'monitor';
    }
}

export async function getMonitorEntryTarget(): Promise<MonitorEntryTarget> {
    if (entryTarget.value && Date.now() - entryTargetCheckedAt < ENTRY_CACHE_MS) {
        return entryTarget.value;
    }

    if (!entryTargetRequest) {
        entryTargetRequest = resolveMonitorEntryTargetFromSources(controlDeferred.promise)
            .then(target => {
                entryTarget.value = target;
                entryTargetCheckedAt = Date.now();
                console.debug('[Cron] Monitor navigation entry resolved:', { target });
                return target;
            })
            .finally(() => {
                entryTargetRequest = undefined;
            });
    }

    return entryTargetRequest;
}
